import { Link } from 'expo-router';
import type { ComponentProps } from 'react';
import { type FragmentOf, readFragment } from '@/gql/graphql';
import type { RsvpStatusEnum } from '@/gql/scalars';
import Box from '../UI/Box';
import * as EventRsvpPicker from './EventRsvpPicker';
import { EventRsvpPickerFragment } from './EventRsvpPicker.types';

type DisplayRsvp = RsvpStatusEnum | 'mixed' | null;

export default function EventRsvpSummary({
  fragment,
  size = 'lg',
}: {
  fragment: FragmentOf<typeof EventRsvpPickerFragment> | null | undefined;
  size?: ComponentProps<typeof EventRsvpPicker.Button>['size'];
}) {
  const event = readFragment(EventRsvpPickerFragment, fragment);
  const statuses = [
    event?.rsvp ?? null,
    ...(event?.managed_rsvps ?? []).map(
      (managedRsvp) => managedRsvp?.status ?? null,
    ),
  ];
  const uniqueStatuses = new Set(
    statuses.filter((status): status is RsvpStatusEnum => status !== null),
  );
  const isMixed = uniqueStatuses.size > 1;
  const displayRsvp: DisplayRsvp = isMixed
    ? 'mixed'
    : (uniqueStatuses.values().next().value ?? null);
  const mixedCount = isMixed
    ? statuses.filter((status) => status !== null).length
    : undefined;

  return (
    <Box px="lg" direction="horizontal" alignItems="center">
      <Link
        asChild
        href={{
          pathname: '/event/[eventId]/rsvp',
          params: { eventId: event?.id ?? '' },
        }}
      >
        <EventRsvpPicker.Button
          accessibilityLabel={`View RSVP details: ${displayRsvp ?? 'no response'}`}
          count={mixedCount}
          isSelected
          rsvp={displayRsvp}
          size={size}
          testID="EventRsvpButton"
          variant={isMixed ? 'secondary' : 'success'}
        />
      </Link>
    </Box>
  );
}
